import {
	useMutation,
	UseMutationResult,
	useQuery,
	UseQueryResult,
} from '@tanstack/react-query'

import { RequestUpgradeRequestDTO } from '@/core/api/dtos/request/account/requestUpgrade'
import { UpdateSubscriptionRequestDTO } from '@/core/api/dtos/request/account/updateSubscription'
import { UpgradeStatusRequestDTO } from '@/core/api/dtos/request/account/upgradeStatus'
import { StatusUpgrade } from '@/core/api/entities/account/statusUpgrade'
import { Subscription } from '@/core/api/entities/account/subscription'
import { SubscriptionPlan } from '@/core/api/entities/account/subscriptionPlan'
import { StatusUpgradeMapper } from '@/core/api/mappers/account/statusUpgradeMapper'
import { SubscriptionMapper } from '@/core/api/mappers/account/subscriptionMapper'
import { SubscriptionPlanMapper } from '@/core/api/mappers/account/subscriptionPlanMapper'
import { subscriptionService } from '@/core/api/services/account/subscriptionService'

import { DEFAULT_PARAMS } from '@core/hooks/common'

/**
 * Get account upgrade status
 */
export const useGetAccountUpgradeStatus = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	StatusUpgrade,
	Error,
	UpgradeStatusRequestDTO,
	any
> => {
	return useMutation<StatusUpgrade, Error, UpgradeStatusRequestDTO, any>({
		mutationFn: (data: UpgradeStatusRequestDTO) =>
			subscriptionService
				.getUpgradeStatus(data, config)
				.then(({ data: response }) => {
					return StatusUpgradeMapper.toEntity(response.data)
				}),
		...options,
	})
}

/**
 * Get subscription plans
 */
export const useGetSubscriptionPlans = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<SubscriptionPlan[], Error> => {
	return useQuery({
		queryKey: [
			'subscription-plans',
			{
				...config?.params,
			},
		],
		queryFn: () =>
			subscriptionService
				.getSubscriptionPlans(config)
				.then(({ data: response }) => {
					return SubscriptionPlanMapper.toEntities(response.data)
				}),
		...options,
		cacheTime: 86400000, // 24h = 24h * 60m * 60s * 1000ms = 86400000ms
		staleTime: 86400000, // 24h = 24h * 60m * 60s * 1000ms = 86400000ms
	})
}

/**
 * Request upgrade
 */
export const useRequestUpgrade = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	RequestUpgradeRequestDTO,
	any
> => {
	return useMutation<void, Error, RequestUpgradeRequestDTO, any>({
		mutationFn: (data: RequestUpgradeRequestDTO) =>
			subscriptionService
				.requestUpgrade(data, config)
				.then(({ data: response }) => {
					return response.data
				}),
		...options,
	})
}

/**
 * Subscribe to a plan (create new subscription or upgrade trial)
 */
export const useSubscribe = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Subscription | void,
	Error,
	any,
	any
> => {
	return useMutation<Subscription | void, Error, any, any>({
		mutationFn: (data: any) =>
			subscriptionService.subscribe(data, config).then(({ data: response }) => {
				// If response has data, map it, otherwise just return void (success)
				if (response?.data) {
					return SubscriptionMapper.toEntity(response.data)
				}
				// If no data returned, just return void - the refetch will get updated data
				return
			}),
		...options,
	})
}

/**
 * Update subscription (change plan, period cycle, or zone quantity)
 *
 * @param subscriptionId - The ID of the subscription to update
 *
 * Note: This endpoint validates the request but may not apply changes yet.
 * It will reject if trying to reduce zones below current usage
 * (e.g., has 10 zones and wants to lower to 8 - rejected until 2 zones are deleted).
 *
 * @example
 * ```typescript
 * const subscription = account.getActiveSubscriptions()[0]
 * const playerItems = subscription.getPlayerItems()
 *
 * const { mutate } = useUpdateSubscription({ subscriptionId: subscription.id })
 *
 * mutate({
 *   plan_id: 123,
 *   period_cycle: PeriodCycle.YEARLY,
 *   items: playerItems.map(item => ({ id: item.id, quantity: newQuantity })),
 *   players: [playerId] // optional: when deleting a zone
 * })
 * ```
 */
export const useUpdateSubscription = ({
	subscriptionId,
	config,
	options,
}: {
	subscriptionId: number
	config?: any
	options?: any
}): UseMutationResult<
	Subscription,
	Error,
	UpdateSubscriptionRequestDTO,
	any
> => {
	return useMutation<Subscription, Error, UpdateSubscriptionRequestDTO, any>({
		mutationFn: (data: UpdateSubscriptionRequestDTO) =>
			subscriptionService
				.updateSubscription(subscriptionId, data, config)
				.then(({ data: response }) => {
					return SubscriptionMapper.toEntity(response.data)
				}),
		...options,
	})
}
